<script setup lang="ts">
import { computed } from 'vue';
import { Dialog } from 'quasar';
import { ITicketFileContext } from '@/components/TicketsAccountingPeriod/ticketsTypes.ts';
import TicketContextFileViewer from '@/components/TicketsAccountingPeriod/TicketContextFileViewer.vue';
import { useFilesActions } from '@/composables/useFilesActions.ts';
import useNavigateFolder from '@/components/TableTransactions/useNavigateFolder.ts';

const props = withDefaults(defineProps<{
  isLoading?: boolean;
  filesContext?: ITicketFileContext[] | null;
  canUnlink?: boolean;
  hideNavigation?: boolean;
}>(), {
  isLoading: false,
  filesContext: null,
  canUnlink: false,
  hideNavigation: false,
});

const emit = defineEmits<{(e: 'unlinkDocument', docId: string[]): void}>();

const { download } = useFilesActions();
const { isPendingLocationDoc, handleNavigateToFolder } = useNavigateFolder();

const filesCountText = computed(() => {
  const count = props.filesContext?.length || 0;
  return `${count} ${count === 1 ? 'file' : 'files'}`;
});

const handleViewFile = (file: ITicketFileContext) => {
  Dialog.create({
    component: TicketContextFileViewer,
    componentProps: {
      fileId: file.id,
      fileName: file.name,
    },
  });
};

const handleDownloadFile = (file: ITicketFileContext) => {
  download(file.id, file.name);
};

const handleNavigateToFolderAction = (file: ITicketFileContext) => {
  handleNavigateToFolder(file.id);
};

const handleUnlinkDocument = (docId: string) => {
  Dialog.create({
    title: 'Confirm Unlink',
    message: 'Are you sure you want to unlink this document from the question?',
    persistent: true,
    ok: {
      label: 'Unlink',
      color: 'negative',
      flat: true,
      noCaps: true,
    },
    cancel: {
      label: 'Cancel',
      color: 'primary',
      flat: true,
      noCaps: true,
    },
  }).onOk(() => {
    const remainingIds = props.filesContext
      ?.filter((file) => file.id !== docId)
      .map((file) => file.id) || [];
    emit('unlinkDocument', remainingIds);
  });
};
</script>

<template>
  <template v-if="props.filesContext?.length">
    <div
      class="q-mb-md"
    >
      <div class="text-body1 q-mb-sm text-grey-9 text-weight-medium flex items-center">
        <q-icon
          name="attach_file"
          size="xs"
          class="q-mr-xs"
        />
        Linked Files ({{ filesCountText }})
      </div>

      <div class="rounded-borders bg-white border-grey-4 overflow-hidden bordered">
        <div
          class="overflow-auto scroll-max-height"
        >
          <q-list
            dense
            class="q-pa-xs"
          >
            <q-item
              v-for="file in props.filesContext"
              :key="file.id"
              v-ripple
              class="q-py-xs context-file-item rounded-borders"
              clickable
              @click="handleViewFile(file)"
            >
              <q-item-section
                avatar
                class="q-pr-sm min-width-24"
              >
                <q-icon
                  name="description"
                  color="primary"
                  size="xs"
                />
              </q-item-section>
              <q-item-section>
                <div class="text-caption text-grey-9 break-all">
                  {{ file.name }}
                </div>
              </q-item-section>
              <q-item-section
                side
                class="view-btn-section"
              >
                <div class="row justify-end items-center no-wrap q-gutter-x-sm">
                  <q-btn
                    flat
                    round
                    color="dark"
                    icon="visibility"
                    size="sm"
                    @click.stop="handleViewFile(file)"
                  >
                    <q-tooltip class="text-body2">
                      View document
                    </q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    color="dark"
                    icon="download"
                    size="sm"
                    @click.stop="handleDownloadFile(file)"
                  >
                    <q-tooltip class="text-body2">
                      Download
                    </q-tooltip>
                  </q-btn>
                  <q-btn
                    v-if="!props.hideNavigation"
                    flat
                    round
                    color="dark"
                    icon="open_in_new"
                    size="sm"
                    :loading="isPendingLocationDoc"
                    :disable="isPendingLocationDoc"
                    @click.stop="handleNavigateToFolderAction(file)"
                  >
                    <q-tooltip class="text-body2">
                      Show in folder
                    </q-tooltip>
                  </q-btn>
                  <q-btn
                    v-if="props.canUnlink"
                    flat
                    round
                    color="dark"
                    icon="link_off"
                    size="sm"
                    :loading="props.isLoading"
                    :disable="props.isLoading"
                    @click.stop="handleUnlinkDocument(file.id)"
                  >
                    <q-tooltip class="text-body2">
                      Unlink document
                    </q-tooltip>
                  </q-btn>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
.bordered {
  border: 1px solid #e0e0e0;
}

.scroll-max-height {
  max-height: 200px;
}

.min-width-24 {
  min-width: 24px;
}

.context-file-item {
  .view-btn-section {
    visibility: hidden;
  }

  &:hover {
    .view-btn-section {
      visibility: visible;
    }
  }
}

.break-all {
  word-break: break-all;
}
</style>
